import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@lib/useAuth';
import { useUserRole } from '@lib/useUserRole';
import { signOut } from '@lib/auth';

function Header() {
  const { user, user_settings } = useAuth();
  const { role } = useUserRole(user?.id ?? null);
  const [open, setOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoPulse, setLogoPulse] = useState(true);
  const menuRef = useRef<HTMLLIElement | null>(null);

  const isProfilePublic = Boolean(user_settings?.display_profile);
  const profileButtonClasses = [
    'flex items-center gap-3 rounded-full px-2 py-1 pr-3 transition focus:outline-none focus:ring-2 focus:ring-offset-0',
    'shadow-sm',
    isProfilePublic
      ? '!bg-emerald-500 !text-white hover:!bg-emerald-600 focus:!ring-emerald-300'
      : '!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-300',
  ].join(' ');

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    function stopPulse() {
      setLogoPulse(false);
    }

    window.addEventListener('load', stopPulse);
    const timer = setTimeout(stopPulse, 1800);

    return () => {
      window.removeEventListener('load', stopPulse);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const profileImageUrl =
    (user_settings?.profile_picture_name &&
      (String(user_settings.profile_picture_name).startsWith('http')
        ? user_settings.profile_picture_name
        : `${(import.meta.env.VITE_SUPABASE_URL || '').replace(
            /\/$/,
            ''
          )}/storage/v1/object/public/${encodeURI(
            String(user_settings.profile_picture_name).replace(/^\/+/, '')
          )}`)) ||
    (user?.user_metadata?.avatar_url ?? '');

  const primaryLinks = [
    { to: '/dashboard', label: 'Start Breathing' },
    { to: '/leaders', label: 'Leaderboard' },
    { to: '/vision-board', label: 'Vision Board' },
  ];

  return (
    <header
      id="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-transparent bg-gradient-to-r from-teal-400 via-purple-600 to-indigo-600 shadow-lg backdrop-blur-md"
    >
      <nav className="container mx-auto flex items-center justify-between px-4 py-2">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <div className="relative h-16 w-16 flex-shrink-0">
            {logoPulse && (
              <span
                aria-hidden
                className="absolute -inset-0.5 -z-10 h-full w-full animate-ping rounded-full bg-gradient-to-r from-teal-300 via-purple-400 to-indigo-400 opacity-70"
              />
            )}
            <img
              src="/pictures/logo-v4.png"
              alt="Xhalin logo"
              className="relative z-10 h-16 w-16 rounded-full object-contain shadow-xl"
            />
          </div>
          <div className="flex items-end gap-2">
            <span className="logo-font text-5xl font-extrabold text-white drop-shadow-md">
              <span className="text-white">X</span>
              <span style={{ color: 'white' }}>hal</span>
              <span className="text-white">in</span>
            </span>
          </div>
        </Link>

        {/* Navigation Links */}
        <div className="flex items-center gap-4">
          <ul className="hidden items-center gap-6 md:flex">
            {primaryLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="font-semibold text-white transition-colors hover:text-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            {!user ? (
              <>
                <li>
                  <Link
                    to="/signin"
                    className="font-semibold text-white transition-colors hover:text-gray-100"
                  >
                    Sign In
                  </Link>
                </li>
                <li>
                  <Link
                    to="/signup"
                    className="rounded-lg bg-white px-4 py-2 text-lg font-semibold transition-colors hover:bg-gray-100"
                  >
                    Join <span className="logo-font font-bold">Xhalin</span>
                  </Link>
                </li>
              </>
            ) : (
              <li className="relative" ref={menuRef}>
                <button
                  className={profileButtonClasses}
                  onClick={() => setOpen((o) => !o)}
                >
                  {profileImageUrl ? (
                    <img
                      src={profileImageUrl}
                      alt={user?.email || 'Profile'}
                      className="h-9 w-9 rounded-full object-cover ring-2 ring-white/70"
                    />
                  ) : (
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-2xl text-white ring-2 ring-white/70">
                      😮‍💨
                    </span>
                  )}
                  {user_settings?.display_name && (
                    <span className="max-w-[7rem] truncate text-sm font-semibold text-white">
                      {user_settings.display_name}
                    </span>
                  )}
                  <svg
                    className={`h-4 w-4 transition-transform ${
                      open ? 'rotate-180' : ''
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {open && (
                  <div className="animate-fade-in absolute right-0 mt-2 w-56 overflow-hidden rounded-xl bg-white py-2 shadow-xl ring-1 ring-black/5">
                    <div className="max-h-[70vh] space-y-0 overflow-y-auto">
                      <div className="px-4 pb-3 pt-2">
                        <p className="truncate text-center text-sm font-bold text-black">
                          {user_settings?.display_name ||
                            user?.user_metadata?.display_name ||
                            user?.email}
                        </p>

                        <p className="mt-2 truncate text-center text-xs text-gray-500">
                          {user_settings?.display_profile ? (
                            <Link
                              to={
                                user_settings?.profile_name
                                  ? `/profile/${encodeURIComponent(
                                      String(user_settings.profile_name)
                                    )}`
                                  : '/profile'
                              }
                              className="font-semibold text-emerald-600 hover:underline"
                              onClick={() => setOpen(false)}
                            >
                              View profile
                            </Link>
                          ) : (
                            <>
                              <p>
                                <Link
                                  to={{
                                    pathname: '/settings',
                                    search: '?tab=profile',
                                  }}
                                  className="font-semibold text-emerald-600 hover:underline"
                                  onClick={() => setOpen(false)}
                                >
                                  Make your profile public{' '}
                                </Link>
                              </p>
                            </>
                          )}
                        </p>
                      </div>

                      <div className="mt-1 border-t border-gray-100 px-4 pt-2 text-xs font-semibold uppercase tracking-wide">
                        My Stuff
                      </div>
                      <Link
                        to="/templates"
                        className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                          📂
                        </span>
                        My Breathing Session Templates
                      </Link>

                      <Link
                        to="/vision-boards"
                        className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                          VB
                        </span>
                        My Vision Boards
                      </Link>

                      <Link
                        to="/sessions"
                        className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                          📝
                        </span>
                        My Breathing Sessions
                      </Link>

                      <Link
                        to="/stats"
                        className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-violet-50 text-violet-600">
                          📊
                        </span>
                        My Breathing Stats
                      </Link>

                      <div className="mt-1 border-t border-gray-100 px-4 pt-2 text-xs font-semibold uppercase tracking-wide">
                        Settings
                      </div>
                      <Link
                        to={{ pathname: '/settings', search: '?tab=account' }}
                        className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                          🧾
                        </span>
                        Account
                      </Link>
                      <Link
                        to={{ pathname: '/settings', search: '?tab=profile' }}
                        className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                          👤
                        </span>
                        Profile
                      </Link>
                      <Link
                        to={{ pathname: '/settings', search: '?tab=reminders' }}
                        className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                          ⏰
                        </span>
                        Breathing Reminders
                      </Link>

                      {role === 'admin' && (
                        <>
                          <div className="mt-1 border-t border-gray-100 px-4 pt-2 text-xs font-semibold uppercase tracking-wide">
                            Admin
                          </div>
                          <Link
                            to="/admin/visits"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              VA
                            </span>
                            Visit Analytics
                          </Link>
                          <Link
                            to="/admin/users"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              👥
                            </span>
                            Users
                          </Link>
                          <Link
                            to="/admin/background-videos"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              🎬
                            </span>
                            Background Videos
                          </Link>
                          <Link
                            to="/admin/music"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              🎵
                            </span>
                            Music
                          </Link>
                          <Link
                            to="/admin/vision-board-backgrounds"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              VB
                            </span>
                            Vision Board Backgrounds
                          </Link>
                          <Link
                            to="/admin/vision-board-templates"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              VT
                            </span>
                            Vision Board Templates
                          </Link>
                          <Link
                            to="/buddha-game"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              🧘
                            </span>
                            Buddha Game
                          </Link>
                          <Link
                            to="/admin/badges"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              🏅
                            </span>
                            Badges
                          </Link>
                          <Link
                            to="/admin/blog"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              📰
                            </span>
                            Blog
                          </Link>
                          <Link
                            to="/admin/features"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              ✨
                            </span>
                            Features
                          </Link>
                          <Link
                            to="/admin/breathing-ratio"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              🌬️
                            </span>
                            Breathing Ratios
                          </Link>
                          <Link
                            to="/admin/breathing-presets"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              🫁
                            </span>
                            Breathing Presets
                          </Link>
                          <Link
                            to="/admin/affirmations"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              🧘‍♂️
                            </span>
                            Affirmations
                          </Link>
                          <Link
                            to="/admin/quotes"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              💬
                            </span>
                            Quotes
                          </Link>
                          <Link
                            to="/admin/feedback"
                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setOpen(false)}
                          >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                              FB
                            </span>
                            Feedback
                          </Link>
                        </>
                      )}

                      <button
                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        onClick={async () => {
                          await signOut();
                          setOpen(false);
                          window.location.href = '/';
                        }}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                          🚪
                        </span>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </li>
            )}
          </ul>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg bg-white/30 p-2 text-white transition hover:bg-white/40 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-0 md:hidden"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label="Toggle mobile menu"
          >
            <span className="sr-only">Toggle mobile menu</span>
            {mobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-white/30 bg-white/90 px-4 pb-5 pt-4 text-gray-900 shadow-lg backdrop-blur-sm md:hidden">
          <div className="flex justify-center pb-3">
            <div className="text-center">
              <p className="text-sm font-semibold text-black">
                {user_settings?.display_name ||
                  user?.user_metadata?.name ||
                  user?.email ||
                  'Welcome'}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                {user_settings?.display_profile ? (
                  <Link
                    to={
                      user_settings?.profile_name
                        ? `/profile/${encodeURIComponent(
                            String(user_settings.profile_name)
                          )}`
                        : '/profile'
                    }
                    className="font-semibold text-emerald-600 hover:underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    View profile
                  </Link>
                ) : (
                  <>
                    Enable your profile.{' '}
                    <Link
                      to={{ pathname: '/settings', search: '?tab=profile' }}
                      className="font-semibold text-emerald-600 hover:underline"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Update settings
                    </Link>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {primaryLinks.map((link) => (
              <Link
                key={`mobile-${link.to}`}
                to={link.to}
                className="rounded-2xl px-4 py-2 text-base font-semibold text-gray-900 transition hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="mt-4 border-t border-gray-200 pt-4">
            {user ? (
              <>
                <div className="mb-3 mt-1 rounded-2xl bg-gray-50 px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-black">
                    My Stuff
                  </p>

                  <div className="mt-6 flex flex-col gap-6">
                    <Link
                      to="/templates"
                      className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Breathing Session Templates
                    </Link>
                    <Link
                      to="/vision-boards"
                      className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Vision Boards
                    </Link>
                    <Link
                      to="/sessions"
                      className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Breathing Sessions
                    </Link>
                    <Link
                      to="/stats"
                      className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Breathing Stats
                    </Link>
                  </div>
                </div>
                {role === 'admin' && (
                  <div className="mb-3 mt-1 rounded-2xl bg-gray-50 px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-black">
                      Admin
                    </p>
                    <div className="mt-6 flex flex-col gap-6">
                      <Link
                        to="/admin/visits"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Visit Analytics
                      </Link>
                      <Link
                        to="/admin/users"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Users
                      </Link>
                      <Link
                        to="/buddha-game"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Buddha Game
                      </Link>
                      <Link
                        to="/admin/blog"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Blog
                      </Link>
                      <Link
                        to="/admin/badges"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Badges
                      </Link>
                      <Link
                        to="/admin/features"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Features
                      </Link>
                      <Link
                        to="/admin/background-videos"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Background Videos
                      </Link>
                      <Link
                        to="/admin/music"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Music
                      </Link>
                      <Link
                        to="/admin/vision-board-backgrounds"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Vision Board Backgrounds
                      </Link>
                      <Link
                        to="/admin/vision-board-templates"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Vision Board Templates
                      </Link>
                      <Link
                        to="/admin/breathing-ratios"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Breathing Ratios
                      </Link>
                      <Link
                        to="/admin/affirmations"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Affirmations
                      </Link>
                      <Link
                        to="/admin/quotes"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Quotes
                      </Link>
                      <Link
                        to="/admin/feedback"
                        className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Feedback
                      </Link>
                    </div>
                  </div>
                )}
                <div className="mb-3 mt-1 rounded-2xl bg-gray-50 px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-black">
                    Settings
                  </p>
                  <div className="mt-6 flex flex-col gap-6">
                    <Link
                      to={{ pathname: '/settings', search: '?tab=account' }}
                      className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Account
                    </Link>
                    <Link
                      to={{ pathname: '/settings', search: '?tab=profile' }}
                      className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to={{ pathname: '/settings', search: '?tab=reminders' }}
                      className="text-sm font-semibold text-gray-800 transition hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Breathing Reminders
                    </Link>
                  </div>
                </div>
                {/* Settings removed per request; settings page links kept elsewhere (Account/Reminders) */}
                <button
                  type="button"
                  className="mt-2 w-full rounded-2xl border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-800 transition hover:bg-gray-100"
                  onClick={async () => {
                    await signOut();
                    setMobileMenuOpen(false);
                    setOpen(false);
                    window.location.href = '/';
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/signin"
                  className="block rounded-2xl px-4 py-2 text-sm font-semibold text-gray-800 transition hover:bg-gray-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="mt-2 block rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2 text-center text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Join <span className="logo-font font-bold">Xhalin</span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
